// Story 2.2 — Admin portfolio composer at /admin/portfolios/new.
// Story 2.3 — Steps 4-5 wired (drop release + bulletin link). The
// Step 3 "Publish portfolio (without drop)" button stays as the
// compose-only path; Step 5 adds "Publish portfolio + Release drop"
// for the compose-then-release-in-one-flow path.
//
// 5-step wizard (the only multi-step wizard in v1 per UX principle 3):
//   Step 1 — Metadata (title + theme + kind + target creator count)
//   Step 2 — Creator selection (multi-select from approved creators)
//   Step 3 — Dossier tier probabilities + per-creator hello text
//   Step 4 — Drop pricing + release timing (Story 2.3)
//   Step 5 — Bulletin link (Story 2.3)

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  SealedDossier,
  Skeleton,
  Slider,
  Textarea,
  WAX_DEFAULT_COLOR,
} from "@greenhold/components";

import {
  useAdminApprovedCreators,
  type ApprovedCreatorRow,
} from "../features/admin/portfolios/useAdminApprovedCreators";
import { CreatorQuickViewDialog } from "../features/admin/portfolios/CreatorQuickViewDialog";
import {
  deleteComposerDraft,
  getComposerDraft,
  upsertComposerDraft,
  type ComposerDraft,
} from "../features/admin/portfolios/composerDrafts";
import {
  AdminPortfolioComposeError,
  useAdminCreatePortfolio,
  type ComposePortfolioCreatorPayload,
} from "../features/admin/portfolios/useAdminCreatePortfolio";
import {
  DropReleaseForm,
  EMPTY_RELEASE_FORM_STATE,
  isFormValid as isReleaseFormValid,
  parseDatetimeLocalToIso,
  type DropReleaseFormState,
} from "../features/admin/drops/DropReleaseForm";
import {
  AdminDropReleaseError,
  useAdminCreateDrop,
  type ReleaseDropRequest,
} from "../features/admin/drops/useAdminCreateDrop";

const TITLE_MAX = 60;
const THEME_MAX = 200;
const DESCRIPTION_MAX = 2000;
const HELLO_MAX = 140;
// Wax-seal preset swatches (0079). Bordeaux first = the default; picking
// it stores null (i.e. "use the component default") rather than the hex,
// so a future default-bordeaux retune reaches old portfolios too. The
// native color input beside the row covers everything else.
const SEAL_COLOR_PRESETS: Array<{ label: string; hex: string }> = [
  { label: "Bordeaux (default)", hex: WAX_DEFAULT_COLOR },
  { label: "Forest", hex: "#1f4d38" },
  { label: "Navy", hex: "#1e3a5f" },
  { label: "Royal purple", hex: "#4a2a6b" },
  { label: "Amber", hex: "#b07d2b" },
  { label: "Onyx", hex: "#33302e" },
];
// Vite dev mirrors the backend's GREENHOLD_MOCK_EXTERNALS floor so a
// local run can compose a 2-creator portfolio; the server keeps
// enforcing 20 whenever mock externals are off.
const MIN_CREATORS = import.meta.env.DEV ? 2 : 20;
const TIER_SUM_TOLERANCE = 0.001;

// No "welcome" kind: the dedicated welcome dossier is retired — new users
// get the weekly free claim instead, which grants a random available
// Regular dossier. Composable kinds are only regular/special.
type Kind = "regular" | "special";

type WizardStep = 1 | 2 | 3 | 4 | 5;

// Hello text is the ONLY thing the composer lets an admin vary per
// creator. Odds of every kind are dossier-level:
//   * which creator you get — always uniform, no knob exists at all
//     (counsel ruling D3, share_weight dropped from the schema);
//   * which TIER you get — one set of probabilities for the whole
//     dossier, below, applied identically to every creator in it.
type CreatorRowState = {
  optional_drop_specific_hello_text: string;
};

const DEFAULT_ROW: CreatorRowState = {
  optional_drop_specific_hello_text: "",
};

type TierDistributionState = {
  common: number;
  preferred: number;
  premium: number;
  founders: number;
};

// Mirrors system_config.tier_distribution_defaults (migration 0051
// rebalance: premium + founders = 5% exactly). This is what the platform
// draws against when the dossier leaves tier_distribution NULL, so it is
// both the slider seed and the "you are deviating from this" reference.
const DEFAULT_TIER_DISTRIBUTION: TierDistributionState = {
  common: 0.75,
  preferred: 0.2,
  premium: 0.049,
  founders: 0.001,
};

const TIER_KEYS = ["common", "preferred", "premium", "founders"] as const;

const STEPS: { num: WizardStep; label: string }[] = [
  // Five steps, permanently: ComposerDraft.step is the literal union
  // 1|2|3|4|5 and sanitize() folds anything else back to 1, so adding a
  // sixth step would silently reset every saved draft to the start.
  // Step 3 is relabelled rather than split — it now holds one dossier-wide
  // odds control plus the per-creator hello texts.
  { num: 1, label: "Metadata" },
  { num: 2, label: "Creators" },
  { num: 3, label: "Odds + hello text" },
  { num: 4, label: "Pricing + release" },
  { num: 5, label: "Bulletin link" },
];

function tierSum(t: TierDistributionState): number {
  return t.common + t.preferred + t.premium + t.founders;
}

// Must stay 0.001 to match TIER_SUM_TOLERANCE in portfolio_service.rs and
// the 0.999..=1.001 gate the reveal re-applies to the resolved
// distribution — a sum this UI accepts but the reveal rejects would be a
// dossier that cannot be opened.
function tierSumValid(t: TierDistributionState): boolean {
  return Math.abs(tierSum(t) - 1) <= TIER_SUM_TOLERANCE;
}

function displayLabel(c: ApprovedCreatorRow): string {
  return c.display_name?.trim() || c.handle || c.user_id.slice(0, 8);
}

export function AdminPortfolioComposerPage() {
  const navigate = useNavigate();
  const approvedQuery = useAdminApprovedCreators();
  const createMut = useAdminCreatePortfolio();
  const releaseMut = useAdminCreateDrop();

  // Multi-draft wiring: /admin/portfolios/new is always a CLEAN SLATE;
  // /admin/portfolios/new?draft=ID resumes a saved draft from the list
  // on the portfolios page. Admin works multiple dossiers in parallel by
  // saving each under its own draft id.
  const [searchParams, setSearchParams] = useSearchParams();
  const draftId = searchParams.get("draft");
  const [loadedDraftId, setLoadedDraftId] = useState<string | null>(null);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);

  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 state
  const [title, setTitle] = useState("");
  const [theme, setTheme] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState<Kind>("regular");
  const [targetCount, setTargetCount] = useState("");
  // Wax-seal base color (0079). null = default bordeaux; the component
  // derives the full pressed-wax ramp from this one hex.
  const [sealColor, setSealColor] = useState<string | null>(null);
  // Open-roster mode: publish with recruiting_status='recruiting' and no
  // minimum-creator floor; creators join via portfolio applications.
  const [recruiting, setRecruiting] = useState(false);

  // Step 2 state
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pickerQuery, setPickerQuery] = useState("");

  // Step 3 state — per-creator hello text, keyed by creator_user_id
  const [rows, setRows] = useState<Record<string, CreatorRowState>>({});
  // Step 3 state — ONE tier distribution for the whole dossier. Unticked
  // sends null, which persists as portfolios.tier_distribution = NULL and
  // means "use the platform default".
  const [tierOverrideEnabled, setTierOverrideEnabled] = useState(false);
  const [tierDistribution, setTierDistribution] =
    useState<TierDistributionState>(DEFAULT_TIER_DISTRIBUTION);

  // Step 4 + Step 5 state (Story 2.3 — shared DropReleaseForm container)
  const [releaseFormState, setReleaseFormState] =
    useState<DropReleaseFormState>(EMPTY_RELEASE_FORM_STATE);

  function applyDraft(d: ComposerDraft) {
    setStep(d.step);
    setTitle(d.title);
    setTheme(d.theme);
    setDescription(d.description);
    setKind(d.kind);
    setTargetCount(d.targetCount);
    setSealColor(d.sealColor ?? null);
    setRecruiting(d.recruiting);
    setSelectedIds(d.selectedIds);
    setPickerQuery("");
    setRows(d.rows);
    setTierOverrideEnabled(d.tierOverrideEnabled);
    setTierDistribution(d.tierDistribution);
    setReleaseFormState(d.releaseFormState);
  }

  function resetWizard() {
    setStep(1);
    setTitle("");
    setTheme("");
    setDescription("");
    setKind("regular");
    setTargetCount("");
    setSealColor(null);
    setRecruiting(false);
    setSelectedIds([]);
    setPickerQuery("");
    setRows({});
    setTierOverrideEnabled(false);
    setTierDistribution(DEFAULT_TIER_DISTRIBUTION);
    setReleaseFormState(EMPTY_RELEASE_FORM_STATE);
  }

  // Sync wizard state with the ?draft= param. Covers first mount, a
  // draft link clicked while the composer is already mounted, and
  // navigating to bare /new (clean slate) from an open draft. The
  // fetch is server-side now, so the load is async + cancel-guarded.
  useEffect(() => {
    if (draftId === loadedDraftId) return;
    if (!draftId) {
      setLoadedDraftId(null);
      setDraftSavedAt(null);
      resetWizard();
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const d = await getComposerDraft(draftId);
        if (cancelled) return;
        if (d) {
          applyDraft(d);
          setLoadedDraftId(d.id);
          setDraftSavedAt(d.saved_at);
        } else {
          toast.error("Draft not found — starting a clean slate.");
          setLoadedDraftId(null);
          setDraftSavedAt(null);
          resetWizard();
          setSearchParams({}, { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          toast.error(`Couldn't load the draft: ${(err as Error).message}`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // applyDraft/resetWizard are stable-by-construction setter bundles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftId, loadedDraftId]);

  async function saveDraft() {
    const id = loadedDraftId ?? crypto.randomUUID();
    const draft: ComposerDraft = {
      id,
      saved_at: new Date().toISOString(),
      step,
      title,
      theme,
      description,
      kind,
      targetCount,
      sealColor,
      recruiting,
      selectedIds,
      rows,
      tierOverrideEnabled,
      tierDistribution,
      releaseFormState,
    };
    try {
      await upsertComposerDraft(draft);
      setLoadedDraftId(id);
      setDraftSavedAt(draft.saved_at);
      if (draftId !== id) setSearchParams({ draft: id }, { replace: true });
      toast.success("Draft saved — available from any machine.");
    } catch (err) {
      toast.error(`Couldn't save the draft: ${(err as Error).message}`);
    }
  }

  function deleteCurrentDraft() {
    if (loadedDraftId) {
      // Fire-and-forget: the wizard resets locally either way; a failed
      // delete just leaves a stale row on the portfolios list.
      void deleteComposerDraft(loadedDraftId).catch(() => {});
    }
    setLoadedDraftId(null);
    setDraftSavedAt(null);
    resetWizard();
    setSearchParams({}, { replace: true });
  }

  // ---- Step 1 validation ----
  const step1Valid = useMemo(() => {
    if (!title.trim() || title.length > TITLE_MAX) return false;
    if (theme.length > THEME_MAX) return false;
    if (description.length > DESCRIPTION_MAX) return false;
    if (targetCount && !/^[0-9]+$/.test(targetCount)) return false;
    if (targetCount && parseInt(targetCount, 10) <= 0) return false;
    return true;
  }, [title, theme, description, targetCount]);

  // ---- Step 2 validation ----
  // Recruiting waives the floor: the roster starts empty (or partial)
  // and fills via portfolio applications.
  const step2Valid = recruiting || selectedIds.length >= MIN_CREATORS;

  // ---- Step 3 validation ----
  // ONE tier check for the dossier (real validation — the server rejects a
  // sum outside ±0.001 and the reveal re-gates it), then the per-creator
  // hello-text length loop, which is the only genuinely per-creator rule
  // left in this step.
  const step3Valid = useMemo(() => {
    if (tierOverrideEnabled && !tierSumValid(tierDistribution)) return false;
    return selectedIds.every((id) => {
      const r = rows[id] ?? DEFAULT_ROW;
      if (
        r.optional_drop_specific_hello_text &&
        r.optional_drop_specific_hello_text.length > HELLO_MAX
      ) {
        return false;
      }
      return true;
    });
  }, [selectedIds, rows, tierOverrideEnabled, tierDistribution]);

  const canPublish = step1Valid && step2Valid && step3Valid && !createMut.isPending;
  const releaseFormValid = isReleaseFormValid(releaseFormState, kind);
  // A recruiting portfolio can't release a drop — drops require the
  // 'composed' state (roster frozen). Publish-only is the recruiting path.
  const canPublishWithRelease =
    canPublish && releaseFormValid && !releaseMut.isPending && !recruiting;

  // ---- Handlers ----
  function toggleCreator(id: string) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
    setRows((prev) => {
      if (prev[id]) return prev;
      return { ...prev, [id]: { ...DEFAULT_ROW } };
    });
  }

  function updateRow(id: string, patch: Partial<CreatorRowState>) {
    setRows((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? DEFAULT_ROW), ...patch },
    }));
  }

  // No creator id: there is one distribution and it belongs to the
  // dossier. Losing that parameter is the point of the change.
  function updateTier(tier: keyof TierDistributionState, value: number) {
    setTierDistribution((prev) => ({ ...prev, [tier]: value }));
  }

  async function handlePublish() {
    const approved = approvedQuery.data?.data ?? [];
    const byId = new Map(approved.map((c) => [c.user_id, c]));
    const creators: ComposePortfolioCreatorPayload[] = selectedIds.map(
      (id, idx) => {
        const row = rows[id] ?? DEFAULT_ROW;
        return {
          creator_user_id: id,
          optional_drop_specific_hello_text:
            row.optional_drop_specific_hello_text.trim() || null,
          position_in_portfolio: idx,
        };
      },
    );
    const _firstSelected = byId.get(selectedIds[0]); // touch for future inline-confirm UI
    void _firstSelected;

    try {
      const result = await createMut.mutateAsync({
        title: title.trim(),
        theme: theme.trim() || null,
        description: description.trim() || null,
        seal_color: sealColor,
        kind,
        target_creator_count: targetCount ? parseInt(targetCount, 10) : null,
        creators,
        // Dossier-level, sent once. null = "use the platform default".
        tier_distribution: tierOverrideEnabled ? tierDistribution : null,
        recruiting,
      });
      if (loadedDraftId) void deleteComposerDraft(loadedDraftId).catch(() => {});
      toast.success(
        recruiting
          ? `Portfolio "${title.trim()}" is now recruiting (${result.data.creator_count} pre-added).`
          : `Portfolio "${title.trim()}" composed with ${result.data.creator_count} creators.`,
      );
      navigate(`/admin/portfolios/${result.data.portfolio_id}`);
    } catch (err) {
      if (err instanceof AdminPortfolioComposeError) {
        const creatorLabel = err.creatorUserId
          ? displayLabel(
              approved.find((c) => c.user_id === err.creatorUserId) ?? {
                user_id: err.creatorUserId,
                display_name: null,
                handle: null,
                region: null,
                language: null,
                creator_approved_at: null,
              },
            )
          : "—";
        switch (err.code) {
          case "PORTFOLIO_INSUFFICIENT_CREATORS":
            toast.error(
              `Need at least ${err.min ?? MIN_CREATORS} creators (you have ${err.provided ?? selectedIds.length}).`,
            );
            break;
          case "PORTFOLIO_CREATOR_NOT_APPROVED":
            toast.error(`Creator ${creatorLabel} is no longer approved.`);
            break;
          case "PORTFOLIO_DUPLICATE_CREATORS":
            toast.error(`Duplicate creator detected: ${creatorLabel}.`);
            break;
          // Dossier-scoped now — deliberately says nothing about a
          // creator, because the rejected value belongs to the whole
          // dossier and naming a creator would re-imply per-creator odds
          // in the one place an admin is paying closest attention. Both
          // codes are accepted: the server-side variant is being renamed
          // alongside the column, and a stale error code should still
          // produce the right sentence rather than the generic fallback.
          case "PORTFOLIO_TIER_OVERRIDES_INVALID":
          case "PORTFOLIO_TIER_DISTRIBUTION_INVALID":
            toast.error(
              `Tier probabilities for this dossier are invalid: ${err.reason ?? "must sum to 1.000 ±0.001"}.`,
            );
            break;
          case "VALIDATION_FAILED":
            toast.error(err.field ? `Invalid ${err.field}: ${err.message}` : err.message);
            break;
          default:
            toast.error(`Compose failed: ${err.message}`);
        }
      } else {
        toast.error(`Compose failed: ${(err as Error).message}`);
      }
    }
  }

  // Story 2.3 — compose-then-release in one click (Step 5 "Publish
  // portfolio + Release drop" button). Sequential 2-call orchestration:
  // call compose first; if it succeeds, call release with the returned
  // portfolio_id. If compose succeeds + release fails, redirect to the
  // detail page so admin can retry release-only from Section 2.
  async function handlePublishWithRelease() {
    const approved = approvedQuery.data?.data ?? [];
    const byId = new Map(approved.map((c) => [c.user_id, c]));
    const creators: ComposePortfolioCreatorPayload[] = selectedIds.map(
      (id, idx) => {
        const row = rows[id] ?? DEFAULT_ROW;
        return {
          creator_user_id: id,
          optional_drop_specific_hello_text:
            row.optional_drop_specific_hello_text.trim() || null,
          position_in_portfolio: idx,
        };
      },
    );

    // Step 1: compose.
    let composeResult;
    try {
      composeResult = await createMut.mutateAsync({
        title: title.trim(),
        theme: theme.trim() || null,
        description: description.trim() || null,
        seal_color: sealColor,
        kind,
        target_creator_count: targetCount ? parseInt(targetCount, 10) : null,
        creators,
        // SECOND publish call site. It must carry tier_distribution too —
        // omitting it here would make compose-then-release silently ship
        // the platform default while the wizard showed custom odds.
        tier_distribution: tierOverrideEnabled ? tierDistribution : null,
        recruiting,
      });
    } catch (err) {
      // Mirror handlePublish error branching for the compose step.
      if (err instanceof AdminPortfolioComposeError) {
        const creatorLabel = err.creatorUserId
          ? displayLabel(
              byId.get(err.creatorUserId) ?? {
                user_id: err.creatorUserId,
                display_name: null,
                handle: null,
                region: null,
                language: null,
                creator_approved_at: null,
              },
            )
          : "—";
        toast.error(
          err.code === "PORTFOLIO_INSUFFICIENT_CREATORS"
            ? `Need at least ${err.min ?? MIN_CREATORS} creators (you have ${err.provided ?? selectedIds.length}).`
            : err.code === "PORTFOLIO_CREATOR_NOT_APPROVED"
              ? `Creator ${creatorLabel} is no longer approved.`
              : err.code === "PORTFOLIO_DUPLICATE_CREATORS"
                ? `Duplicate creator detected: ${creatorLabel}.`
                : err.code === "PORTFOLIO_TIER_OVERRIDES_INVALID" ||
                    err.code === "PORTFOLIO_TIER_DISTRIBUTION_INVALID"
                  ? `Tier probabilities for this dossier are invalid: ${err.reason ?? "must sum to 1.000 ±0.001"}.`
                  : err.code === "VALIDATION_FAILED"
                    ? err.field
                      ? `Invalid ${err.field}: ${err.message}`
                      : err.message
                    : `Compose failed: ${err.message}`,
        );
      } else {
        toast.error(`Compose failed: ${(err as Error).message}`);
      }
      return;
    }

    const portfolioId = composeResult.data.portfolio_id;
    // The portfolio exists from here on — the draft is spent even if the
    // release half fails (admin retries release from the detail page).
    if (loadedDraftId) deleteComposerDraft(loadedDraftId);

    // Step 2: release.
    const priceCents = parseInt(releaseFormState.price_cents_input, 10);
    const perUserCap =
      kind === "special"
        ? parseInt(releaseFormState.per_user_cap_input, 10)
        : null;
    const scheduledAtIso = releaseFormState.release_now
      ? new Date().toISOString()
      : parseDatetimeLocalToIso(releaseFormState.scheduled_at_input);
    const retiringAtIso = releaseFormState.retiring_at_input
      ? parseDatetimeLocalToIso(releaseFormState.retiring_at_input)
      : null;

    if (!scheduledAtIso) {
      toast.error("Pick a valid scheduled time.");
      navigate(`/admin/portfolios/${portfolioId}`);
      return;
    }

    const releaseRequest: ReleaseDropRequest = {
      portfolio_id: portfolioId,
      kind,
      price_cents: priceCents,
      per_user_cap: perUserCap,
      scheduled_at: scheduledAtIso,
      retiring_at: retiringAtIso,
      release_now: releaseFormState.release_now,
      linked_bulletin_post_id: releaseFormState.link_bulletin
        ? releaseFormState.linked_bulletin_post_id
        : null,
    };

    try {
      const releaseResult = await releaseMut.mutateAsync(releaseRequest);
      toast.success(
        `Portfolio "${title.trim()}" composed + drop released (${releaseResult.data.status}).`,
      );
      navigate(`/admin/portfolios/${portfolioId}`);
    } catch (err) {
      if (err instanceof AdminDropReleaseError) {
        const msg =
          err.code === "DROP_PORTFOLIO_NOT_COMPOSED"
            ? "Portfolio not in 'composed' state."
            : err.code === "DROP_KIND_MISMATCH_PORTFOLIO_KIND"
              ? `Drop kind '${err.dropKind}' doesn't match portfolio kind '${err.portfolioKind}'.`
              : err.code === "DROP_ALREADY_EXISTS_FOR_PORTFOLIO"
                ? "A drop already exists for this portfolio."
                : err.code === "DROP_PER_USER_CAP_INVALID"
                  ? `per_user_cap invalid for ${err.kindEcho ?? kind} drop.`
                  : err.code === "DROP_INVALID_TIMESTAMP"
                    ? `Timestamp invalid: ${err.reason ?? "see field"}.`
                    : err.code === "DROP_BULLETIN_POST_NOT_FOUND"
                      ? "Linked Bulletin post not found."
                      : err.code === "DROP_BULLETIN_POST_NOT_PUBLISHED"
                        ? "Linked Bulletin post is still a draft — publish it first."
                        : err.code === "DROP_BULLETIN_POST_ALREADY_LINKED"
                          ? "That Bulletin post is already linked to another drop."
                          : err.code === "VALIDATION_FAILED"
                            ? err.field
                              ? `Invalid ${err.field}: ${err.message}`
                              : err.message
                            : `Release failed: ${err.message}`;
        toast.error(
          `Portfolio composed but release failed — ${msg}. Retry from the detail page.`,
        );
      } else {
        toast.error(
          `Portfolio composed but release failed: ${(err as Error).message}. Retry from the detail page.`,
        );
      }
      navigate(`/admin/portfolios/${portfolioId}`);
    }
  }

  // ---- Render ----
  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-wider font-mono opacity-60">
            <Link to="/admin/portfolios" className="underline decoration-dotted">
              ← All portfolios
            </Link>
          </p>
          <h1 className="text-3xl md:text-4xl font-heading font-bold mt-2 mb-2">
            Compose new portfolio
          </h1>
          <p className="opacity-80">
            Bundle approved creators into a buyable dossier. Story 2.3 turns
            this composed portfolio into a released drop.
          </p>
          {loadedDraftId ? (
            <p className="mt-2 text-sm font-mono text-brand-forest-700">
              Editing draft
              {draftSavedAt
                ? ` (saved ${new Date(draftSavedAt).toLocaleString()})`
                : ""}
              .{" "}
              <button
                type="button"
                onClick={deleteCurrentDraft}
                className="underline decoration-dotted"
              >
                Delete draft
              </button>
            </p>
          ) : null}
        </header>

        <StepIndicator step={step} setStep={setStep} canAdvance={[step1Valid, step2Valid, step3Valid]} />

        <div className="mt-8">
          {step === 1 ? (
            <Step1Metadata
              title={title}
              setTitle={setTitle}
              theme={theme}
              setTheme={setTheme}
              description={description}
              setDescription={setDescription}
              kind={kind}
              setKind={setKind}
              targetCount={targetCount}
              setTargetCount={setTargetCount}
              sealColor={sealColor}
              setSealColor={setSealColor}
              recruiting={recruiting}
              setRecruiting={setRecruiting}
            />
          ) : null}
          {step === 2 ? (
            <Step2Creators
              query={approvedQuery}
              selectedIds={selectedIds}
              toggleCreator={toggleCreator}
              pickerQuery={pickerQuery}
              setPickerQuery={setPickerQuery}
              recruiting={recruiting}
            />
          ) : null}
          {step === 3 ? (
            <Step3OddsAndHello
              approved={approvedQuery.data?.data ?? []}
              selectedIds={selectedIds}
              rows={rows}
              updateRow={updateRow}
              tierOverrideEnabled={tierOverrideEnabled}
              setTierOverrideEnabled={setTierOverrideEnabled}
              tierDistribution={tierDistribution}
              updateTier={updateTier}
            />
          ) : null}
          {step === 4 || step === 5 ? (
            <DropReleaseForm
              portfolioKind={kind}
              mode="wizard-step"
              state={releaseFormState}
              setState={setReleaseFormState}
            />
          ) : null}
        </div>

        <Footer
          step={step}
          setStep={setStep}
          step1Valid={step1Valid}
          step2Valid={step2Valid}
          step3Valid={step3Valid}
          canPublish={canPublish}
          canPublishWithRelease={canPublishWithRelease}
          releaseFormValid={releaseFormValid}
          isPending={createMut.isPending || releaseMut.isPending}
          recruiting={recruiting}
          onPublish={handlePublish}
          onPublishWithRelease={handlePublishWithRelease}
          onSaveDraft={saveDraft}
        />
      </div>
    </main>
  );
}

// =============================================================================
// Step indicator
// =============================================================================

function StepIndicator({
  step,
  setStep,
  canAdvance,
}: {
  step: WizardStep;
  setStep: (n: WizardStep) => void;
  canAdvance: [boolean, boolean, boolean];
}) {
  return (
    <ol className="flex flex-wrap items-center gap-3" role="list">
      {STEPS.map((s) => {
        const isCurrent = s.num === step;
        return (
          <li key={s.num}>
            <button
              type="button"
              aria-current={isCurrent ? "step" : undefined}
              onClick={() => setStep(s.num)}
              className={[
                "flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                isCurrent
                  ? "border-brand-forest-700 bg-brand-forest-700 text-brand-paper-100"
                  : "border-brand-forest-200 bg-brand-paper-100 text-brand-forest-900 hover:bg-brand-paper-200",
              ].join(" ")}
            >
              <span className="font-mono text-xs">{s.num}.</span>
              {s.label}
            </button>
          </li>
        );
      })}
      <li className="text-xs opacity-60 font-mono ml-2">
        {canAdvance[0] && canAdvance[1] && canAdvance[2]
          ? "compose-ready"
          : "complete steps 1-3 to publish"}
      </li>
    </ol>
  );
}

// =============================================================================
// Step 1 — Metadata
// =============================================================================

function Step1Metadata({
  title,
  setTitle,
  theme,
  setTheme,
  description,
  setDescription,
  kind,
  setKind,
  targetCount,
  setTargetCount,
  sealColor,
  setSealColor,
  recruiting,
  setRecruiting,
}: {
  title: string;
  setTitle: (v: string) => void;
  theme: string;
  setTheme: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  kind: Kind;
  setKind: (k: Kind) => void;
  targetCount: string;
  setTargetCount: (v: string) => void;
  sealColor: string | null;
  setSealColor: (v: string | null) => void;
  recruiting: boolean;
  setRecruiting: (v: boolean) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Step 1 — Metadata</CardTitle>
        <CardDescription>
          The portfolio's name, theme, kind, and an optional target creator
          count.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="title">Title</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. May 2026 Streamer Dossier"
            maxLength={TITLE_MAX}
            required
          />
          <p className="text-xs opacity-60 mt-1">
            {title.length}/{TITLE_MAX}
          </p>
        </div>
        <div>
          <Label htmlFor="theme">Theme (optional)</Label>
          <Input
            id="theme"
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            placeholder="e.g. Top FPS streamers, EU region"
            maxLength={THEME_MAX}
          />
          <p className="text-xs opacity-60 mt-1">
            {theme.length}/{THEME_MAX}
          </p>
        </div>
        <div>
          <Label htmlFor="description">Description (optional)</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxCharacters={DESCRIPTION_MAX}
            rows={4}
            placeholder="The dossier's own blurb — shown verbatim on the shop hero. Leave blank to render no description (the hero composes no filler)."
          />
          <p className="text-xs opacity-60 mt-1">
            {description.length}/{DESCRIPTION_MAX}
          </p>
        </div>
        <div>
          <Label>Kind</Label>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <KindRadio current={kind} setCurrent={setKind} value="regular" label="Regular" hint="Uncapped, ~6mo shop life" />
            <KindRadio current={kind} setCurrent={setKind} value="special" label="Special" hint="Per-user cap + time-window" />
          </div>
        </div>
        <div>
          <Label htmlFor="seal-color">Wax-seal color</Label>
          <div className="mt-2 flex flex-wrap items-start gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {SEAL_COLOR_PRESETS.map((preset) => {
                  const selected =
                    (sealColor ?? WAX_DEFAULT_COLOR) === preset.hex;
                  return (
                    <button
                      key={preset.hex}
                      type="button"
                      title={preset.label}
                      aria-label={`Seal color ${preset.label}`}
                      aria-pressed={selected}
                      onClick={() =>
                        setSealColor(
                          preset.hex === WAX_DEFAULT_COLOR ? null : preset.hex,
                        )
                      }
                      className={[
                        "h-8 w-8 rounded-full border transition-shadow",
                        selected
                          ? "border-brand-forest-700 ring-2 ring-brand-forest-700 ring-offset-2"
                          : "border-brand-forest-200 hover:ring-2 hover:ring-brand-forest-300 hover:ring-offset-2",
                      ].join(" ")}
                      style={{ background: preset.hex }}
                    />
                  );
                })}
                <input
                  id="seal-color"
                  type="color"
                  value={sealColor ?? WAX_DEFAULT_COLOR}
                  onChange={(e) => {
                    const hex = e.target.value.toLowerCase();
                    setSealColor(hex === WAX_DEFAULT_COLOR ? null : hex);
                  }}
                  className="h-8 w-10 cursor-pointer rounded border border-brand-forest-200 bg-transparent p-0.5"
                  aria-label="Custom seal color"
                />
              </div>
              <p className="text-xs opacity-60 m-0">
                One base color per portfolio — the pressed-wax shading,
                rim ink and monogram all derive from it. Leave on bordeaux
                for the classic seal.{" "}
                {sealColor ? (
                  <button
                    type="button"
                    className="underline"
                    onClick={() => setSealColor(null)}
                  >
                    Reset to bordeaux
                  </button>
                ) : null}
              </p>
            </div>
            {/* Live preview — the real artifact at thumb size. */}
            <div className="w-[72px] shrink-0">
              <SealedDossier
                size="thumb"
                title={title.trim() || "Preview"}
                sealColor={sealColor}
              />
            </div>
          </div>
        </div>
        <div>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand-forest-700"
              checked={recruiting}
              onChange={(e) => setRecruiting(e.target.checked)}
            />
            <span>
              <span className="font-medium">Open for recruiting</span>
              <span className="block text-xs opacity-70 mt-0.5">
                Publish with an open roster — no creator minimum (even zero).
                Creators join via portfolio applications; the drop releases
                later, once recruiting closes. &ldquo;Publish + Release
                drop&rdquo; is unavailable in this mode.
              </span>
            </span>
          </label>
        </div>
        <div>
          <Label htmlFor="target-count">Target creator count (optional)</Label>
          <Input
            id="target-count"
            value={targetCount}
            onChange={(e) => setTargetCount(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="e.g. 25"
            inputMode="numeric"
            pattern="[0-9]*"
          />
          <p className="text-xs opacity-60 mt-1">
            Informational only at v1 — the SPA's Step 2 still enforces ≥
            {MIN_CREATORS} creators per portfolio.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function KindRadio({
  current,
  setCurrent,
  value,
  label,
  hint,
  disabled,
}: {
  current: Kind;
  setCurrent: (k: Kind) => void;
  value: Kind;
  label: string;
  hint: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => !disabled && setCurrent(value)}
      className={[
        "rounded-lg border-2 px-4 py-3 text-left transition-colors min-w-[180px]",
        disabled
          ? "border-brand-forest-200 bg-brand-paper-200 opacity-50 cursor-not-allowed"
          : current === value
            ? "border-brand-forest-700 bg-brand-paper-100"
            : "border-brand-forest-200 bg-brand-paper-100 hover:border-brand-forest-300",
      ].join(" ")}
    >
      <p className="font-medium">{label}</p>
      <p className="text-xs opacity-70 mt-1">{hint}</p>
    </button>
  );
}

// =============================================================================
// Step 2 — Creators
// =============================================================================

function Step2Creators({
  query,
  selectedIds,
  toggleCreator,
  pickerQuery,
  setPickerQuery,
  recruiting,
}: {
  query: ReturnType<typeof useAdminApprovedCreators>;
  selectedIds: string[];
  toggleCreator: (id: string) => void;
  pickerQuery: string;
  setPickerQuery: (v: string) => void;
  recruiting: boolean;
}) {
  const all = query.data?.data ?? [];
  const filtered = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => {
      const label = `${c.display_name ?? ""} ${c.handle ?? ""}`.toLowerCase();
      return label.includes(q);
    });
  }, [all, pickerQuery]);

  const belowMin = !recruiting && selectedIds.length < MIN_CREATORS;

  // Quick-view popup target — profile inspection without leaving Step 2.
  const [previewCreator, setPreviewCreator] =
    useState<ApprovedCreatorRow | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step 2 — Select creators</CardTitle>
        <CardDescription>
          {recruiting
            ? "Recruiting mode — pre-adding creators is optional (the roster fills via portfolio applications). Max 200."
            : `Pick the approved creators to include. Minimum ${MIN_CREATORS}; max 200.`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="picker-search">Search by display name or handle</Label>
          <Input
            id="picker-search"
            value={pickerQuery}
            onChange={(e) => setPickerQuery(e.target.value)}
            placeholder="Type to filter…"
            aria-label="Search approved creators by display name"
          />
        </div>
        <p
          className={[
            "text-sm font-medium",
            belowMin ? "text-destructive" : "text-brand-forest-700",
          ].join(" ")}
        >
          {recruiting
            ? `${selectedIds.length} pre-added (optional) ✓`
            : `${selectedIds.length} / ${MIN_CREATORS} selected${
                belowMin ? ` (need at least ${MIN_CREATORS})` : " ✓"
              }`}
        </p>
        {query.isPending ? (
          <div className="grid gap-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-12 w-full rounded-md" />
            ))}
          </div>
        ) : query.isError ? (
          <p className="text-destructive text-sm">
            Couldn't load approved creators. Refresh the page or check the
            admin endpoint.
          </p>
        ) : filtered.length === 0 ? (
          <p className="opacity-70 text-sm">No creators match the filter.</p>
        ) : (
          <ul className="grid gap-2 max-h-[480px] overflow-y-auto pr-2" role="list">
            {filtered.map((c) => {
              const checked = selectedIds.includes(c.user_id);
              return (
                <li
                  key={c.user_id}
                  className="flex items-center gap-2 rounded-md border border-brand-forest-200 bg-brand-paper-100 px-3 py-2 hover:bg-brand-paper-200"
                >
                  {/* The view button sits OUTSIDE the label — a button
                      inside it would also toggle the checkbox. */}
                  <label className="flex flex-1 items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCreator(c.user_id)}
                      className="h-4 w-4 accent-brand-forest-700"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{displayLabel(c)}</p>
                      <p className="text-xs opacity-60">
                        {c.handle ? `@${c.handle}` : "no handle"}
                        {c.region ? ` · ${c.region}` : ""}
                        {c.language ? ` · ${c.language}` : ""}
                      </p>
                    </div>
                  </label>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setPreviewCreator(c)}
                    aria-label={`View profile for ${displayLabel(c)}`}
                  >
                    View
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
        <CreatorQuickViewDialog
          creator={previewCreator}
          onClose={() => setPreviewCreator(null)}
        />
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Step 3 — Dossier tier probabilities + per-creator hello text
// =============================================================================
//
// ONE odds card for the dossier, then the per-creator hello texts. This
// used to be one odds card PER CREATOR — twenty near-identical slider
// blocks whose own header insisted creators were interchangeable. Odds
// belong to the dossier, so there is exactly one control for them and no
// way to express a per-creator value.

function Step3OddsAndHello({
  approved,
  selectedIds,
  rows,
  updateRow,
  tierOverrideEnabled,
  setTierOverrideEnabled,
  tierDistribution,
  updateTier,
}: {
  approved: ApprovedCreatorRow[];
  selectedIds: string[];
  rows: Record<string, CreatorRowState>;
  updateRow: (id: string, patch: Partial<CreatorRowState>) => void;
  tierOverrideEnabled: boolean;
  setTierOverrideEnabled: (v: boolean) => void;
  tierDistribution: TierDistributionState;
  updateTier: (tier: keyof TierDistributionState, value: number) => void;
}) {
  const byId = new Map(approved.map((c) => [c.user_id, c]));
  const sum = tierSum(tierDistribution);
  const valid = tierSumValid(tierDistribution);
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Tier probabilities for this dossier</CardTitle>
          <CardDescription>
            The same for every creator in it. Every creator in this dossier
            has an equal chance of being revealed — always — and these
            probabilities decide which certificate TIER is issued, not which
            creator appears. One set of odds for the whole dossier: they do
            not change depending on who you get. Leave the box unticked to
            use the platform defaults (75 / 20 / 4.9 / 0.1).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 accent-brand-forest-700"
              checked={tierOverrideEnabled}
              onChange={(e) => setTierOverrideEnabled(e.target.checked)}
            />
            <span>Override default tier probabilities for this dossier</span>
          </label>

          {tierOverrideEnabled ? (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {TIER_KEYS.map((tier) => (
                <div key={tier}>
                  <Label htmlFor={`tier-${tier}`}>
                    {tier.charAt(0).toUpperCase() + tier.slice(1)}:{" "}
                    {(tierDistribution[tier] * 100).toFixed(2)}%
                  </Label>
                  <Slider
                    id={`tier-${tier}`}
                    min={0}
                    max={1}
                    step={0.001}
                    value={[tierDistribution[tier]]}
                    onValueChange={(v) => updateTier(tier, v[0] ?? 0)}
                    aria-label={`${tier} tier probability for this dossier`}
                  />
                  {/* Ghost reference so "override" means something visible
                      — the admin can see what they are deviating from. */}
                  <p className="mt-1 text-xs font-mono opacity-60">
                    platform default{" "}
                    {(DEFAULT_TIER_DISTRIBUTION[tier] * 100).toFixed(2)}%
                  </p>
                </div>
              ))}
              <p
                role="status"
                aria-live="polite"
                className={[
                  "md:col-span-2 text-sm font-mono",
                  valid ? "text-brand-forest-700" : "text-destructive",
                ].join(" ")}
              >
                Sum: {sum.toFixed(4)} {valid ? "✓" : "✗ must be 1.000 ±0.001"}
              </p>
            </div>
          ) : (
            <p className="mt-3 text-sm opacity-70">
              Using the platform defaults — Common 75%, Preferred 20%,
              Premium 4.9%, Founder&apos;s 0.1%. These are published to
              buyers on the dossier&apos;s shop page.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Hello text</CardTitle>
          <CardDescription>
            The one thing that varies per creator. Optional — leave blank to
            use the creator&apos;s profile hello-text.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {selectedIds.length === 0 ? (
            <p className="opacity-70 text-sm">
              Go back to Step 2 to select creators first.
            </p>
          ) : (
            selectedIds.map((id) => {
              const c = byId.get(id);
              const row = rows[id] ?? DEFAULT_ROW;
              return (
                <div
                  key={id}
                  className="rounded-lg border border-brand-forest-200 bg-brand-paper-100 p-4"
                >
                  <div>
                    <p className="font-medium">
                      {c ? displayLabel(c) : id.slice(0, 8)}
                    </p>
                    {c?.handle ? (
                      <p className="text-xs opacity-60">@{c.handle}</p>
                    ) : null}
                  </div>

                  <div className="mt-4">
                    <Label htmlFor={`hello-${id}`}>
                      Optional drop-specific hello text (≤{HELLO_MAX} chars)
                    </Label>
                    <Textarea
                      id={`hello-${id}`}
                      value={row.optional_drop_specific_hello_text}
                      onChange={(e) =>
                        updateRow(id, {
                          optional_drop_specific_hello_text: e.target.value,
                        })
                      }
                      maxCharacters={HELLO_MAX}
                      rows={2}
                      placeholder="Leave blank to use the creator's profile hello-text."
                    />
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// Placeholder steps + footer
// =============================================================================

function PlaceholderStep({ story, body }: { story: string; body: string }) {
  return (
    <Card>
      <CardContent className="p-8 text-center space-y-3">
        <Badge variant="secondary" className="font-mono">
          Coming with Story {story}
        </Badge>
        <p className="opacity-80 max-w-xl mx-auto leading-relaxed">{body}</p>
        <p className="text-xs opacity-60 italic">
          The wizard renders this step structurally so admin sees the full
          pipeline; publishing today happens at the end of Step 3.
        </p>
      </CardContent>
    </Card>
  );
}

function Footer({
  step,
  setStep,
  step1Valid,
  step2Valid,
  step3Valid,
  canPublish,
  canPublishWithRelease,
  releaseFormValid,
  isPending,
  recruiting,
  onPublish,
  onPublishWithRelease,
  onSaveDraft,
}: {
  step: WizardStep;
  setStep: (n: WizardStep) => void;
  step1Valid: boolean;
  step2Valid: boolean;
  step3Valid: boolean;
  canPublish: boolean;
  canPublishWithRelease: boolean;
  releaseFormValid: boolean;
  isPending: boolean;
  recruiting: boolean;
  onPublish: () => void;
  onPublishWithRelease: () => void;
  onSaveDraft: () => void;
}) {
  const canNext =
    (step === 1 && step1Valid) ||
    (step === 2 && step2Valid) ||
    step === 3 ||
    step === 4;
  return (
    <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3">
        {step > 1 ? (
          <Button
            variant="secondary"
            onClick={() => setStep((step - 1) as WizardStep)}
          >
            ← Back
          </Button>
        ) : null}
        {/* Draft saves the raw wizard state, so it's allowed on any step
            regardless of validation. */}
        <Button variant="tertiary" onClick={onSaveDraft} disabled={isPending}>
          Save draft
        </Button>
      </div>
      <div className="flex items-center gap-3">
        {step < 5 ? (
          <Button
            variant="primary"
            disabled={!canNext}
            onClick={() => setStep((step + 1) as WizardStep)}
          >
            Next →
          </Button>
        ) : null}
        {step >= 3 ? (
          <Button
            variant="secondary"
            disabled={!canPublish}
            onClick={onPublish}
            aria-describedby="publish-hint"
          >
            {isPending ? "Working…" : "Publish portfolio (without drop)"}
          </Button>
        ) : null}
        {step === 5 ? (
          <Button
            variant="primary"
            disabled={!canPublishWithRelease}
            onClick={onPublishWithRelease}
            aria-describedby="publish-hint"
          >
            {isPending ? "Working…" : "Publish portfolio + Release drop"}
          </Button>
        ) : null}
      </div>
      {step >= 3 && !isPending ? (
        <p id="publish-hint" className="w-full text-xs opacity-70 text-right">
          {!step1Valid
            ? "Step 1 metadata is incomplete."
            : !step2Valid
              ? `Step 2: select at least ${MIN_CREATORS} creators.`
              : !step3Valid
                ? "Step 3: the dossier's tier probabilities must sum to 1.000 ±0.001, and hello text must fit."
                : step === 5 && recruiting
                  ? "Recruiting mode publishes without a drop — release comes after recruiting closes into a composed roster."
                  : step === 5 && !releaseFormValid
                    ? "Step 4+5: pricing + release timing (+ cap for special) must be valid before releasing."
                    : ""}
        </p>
      ) : null}
    </div>
  );
}
